import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Play, 
  Pause, 
  Volume, 
  Volume1, 
  Volume2, 
  VolumeX, 
  Maximize, 
  Minimize, 
  Settings, 
  SkipBack, 
  SkipForward,
  Eye,
  EyeOff,
  X,
  FileText,

  Video,
  MessageSquare
} from 'lucide-react';
import { convertToEmbedUrl, isValidVideoUrl, getVideoPlatform } from '../utils/videoUtils';
import DiscussionPanel from './DiscussionPanel';

const VideoPlayer = ({ 
  src, 
  poster, 
  title = "Course Lecture",
  onTimeUpdate,
  onEnded,
  onError,
  initialVolume = 0.7,
  autoPlay = false,
  enableControls = true,
  className = "",
  onBookmark,
  bookmarks = [],
  onProgress,
  quality = "auto",
  attachments = [], // New prop for lecture attachments
  lectureId = null, // New prop for lecture ID
  onDiscussionToggle = null, // New prop for discussion toggle callback
  showDiscussion = false, // New prop to control discussion panel visibility
  currentUser = null // New prop for current user
}) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const progressRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(initialVolume);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false); // Start in normal mode
  const [showSettings, setShowSettings] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [buffered, setBuffered] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showControls, setShowControls] = useState(true); // Show controls initially
  const [controlsTimeout, setControlsTimeout] = useState(null);
  const [currentQuality, setCurrentQuality] = useState(quality);
  const [showNotes, setShowNotes] = useState(false);
  const [isEmbeddedVideo, setIsEmbeddedVideo] = useState(false);

  // Available playback speeds
  const playbackSpeeds = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

  // Quality options (you can customize based on your video sources)
  const qualityOptions = [
    { label: "Auto", value: "auto" },
    { label: "1080p", value: "1080" },
    { label: "720p", value: "720" },
    { label: "480p", value: "480" },
    { label: "360p", value: "360" }
  ];

  // Format time to MM:SS
  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Handle play/pause
  const togglePlay = useCallback(() => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
    }
  }, [isPlaying]);

  // Handle volume change
  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
    }
    setIsMuted(newVolume === 0);
  };

  // Handle mute/unmute
  const toggleMute = () => {
    if (videoRef.current) {
      if (isMuted) {
        videoRef.current.volume = volume;
        setIsMuted(false);
      } else {
        videoRef.current.volume = 0;
        setIsMuted(true);
      }
    }
  };

  // Handle fullscreen with proper browser API
  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await containerRef.current?.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (error) {
      // Fullscreen error
      // Fallback to custom fullscreen
      setIsFullscreen(!isFullscreen);
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isInFullscreen = !!document.fullscreenElement;
      setIsFullscreen(isInFullscreen);
      
      // If we're not in fullscreen but our state says we are, update it
      if (!isInFullscreen && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, [isFullscreen]);

  // Handle progress bar click
  const handleProgressClick = (e) => {
    if (progressRef.current && videoRef.current) {
      const rect = progressRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const width = rect.width;
      const percentage = clickX / width;
      const newTime = percentage * duration;
      videoRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  // Handle playback rate change
  const handlePlaybackRateChange = (rate) => {
    if (videoRef.current) {
      videoRef.current.playbackRate = rate;
      setPlaybackRate(rate);
    }
    setShowSettings(false);
  };

  // Handle quality change
  const handleQualityChange = (newQuality) => {
    setCurrentQuality(newQuality);
    setShowSettings(false);
    // Here you would typically switch video sources
    // For now, we'll just update the state
  };

  // Skip forward/backward
  const skipTime = (seconds) => {
    if (videoRef.current) {
      const newTime = Math.max(0, Math.min(duration, currentTime + seconds));
      videoRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  // Handle video events
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
      setIsLoading(false);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
      if (onTimeUpdate) {
        onTimeUpdate(video.currentTime);
      }
    };

    const handleProgress = () => {
      if (video.buffered.length > 0) {
        const bufferedEnd = video.buffered.end(video.buffered.length - 1);
        const bufferedPercent = (bufferedEnd / duration) * 100;
        setBuffered(bufferedPercent);
      }
    };

    const handlePlay = () => {
      setIsPlaying(true);
      // Auto-hide controls after 3 seconds when playing
      const timeout = setTimeout(() => {
        setShowControls(false);
      }, 3000);
      setControlsTimeout(timeout);
    };
    const handlePause = () => {
      setIsPlaying(false);
      // Show controls when paused
      setShowControls(true);
      if (controlsTimeout) {
        clearTimeout(controlsTimeout);
        setControlsTimeout(null);
      }
    };
    const handleEnded = () => {
      setIsPlaying(false);
      // Show controls when video ends
      setShowControls(true);
      if (controlsTimeout) {
        clearTimeout(controlsTimeout);
        setControlsTimeout(null);
      }
      if (onEnded) onEnded();
    };
    const handleError = (e) => {
      setError(e);
      if (onError) onError(e);
    };

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('progress', handleProgress);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('error', handleError);

    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('progress', handleProgress);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('error', handleError);
    };
  }, [onTimeUpdate, onEnded, onError, duration]);

  // Check if video is embedded (YouTube, Vimeo, etc.)
  useEffect(() => {
    setIsEmbeddedVideo(isValidVideoUrl(src));
  }, [src]);

  // Auto-hide loading after a timeout
  useEffect(() => {
    if (isLoading) {
      const timeout = setTimeout(() => {
        setIsLoading(false);
      }, 10000); // 10 seconds timeout
      
      return () => clearTimeout(timeout);
    }
  }, [isLoading]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!containerRef.current?.contains(document.activeElement)) return;

      switch (e.code) {
        case 'Space':
          e.preventDefault();
          togglePlay();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          skipTime(-10);
          break;
        case 'ArrowRight':
          e.preventDefault();
          skipTime(10);
          break;
        case 'ArrowUp':
          e.preventDefault();
          handleVolumeChange({ target: { value: Math.min(1, volume + 0.1) } });
          break;
        case 'ArrowDown':
          e.preventDefault();
          handleVolumeChange({ target: { value: Math.max(0, volume - 0.1) } });
          break;
        case 'KeyM':
          e.preventDefault();
          toggleMute();
          break;
        case 'KeyF':
          e.preventDefault();
          toggleFullscreen();
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [togglePlay, volume, toggleMute, toggleFullscreen]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (controlsTimeout) {
        clearTimeout(controlsTimeout);
      }
    };
  }, [controlsTimeout]);

  if (error) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-100 dark:bg-gray-800 rounded-lg">
        <div className="text-center">
          <Play className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <div className="text-gray-600 dark:text-gray-300 text-lg font-medium mb-2">Lecture Content</div>
          <div className="text-gray-500 dark:text-gray-400 mb-4">This lecture is available for viewing</div>
          <button 
            onClick={() => setError(null)} 
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!src) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-100 dark:bg-gray-800 rounded-lg">
        <div className="text-center">
          <Play className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <div className="text-gray-600 dark:text-gray-300 text-lg font-medium mb-2">Lecture Content</div>
          <div className="text-gray-500 dark:text-gray-400 mb-4">Select a lecture to begin</div>
        </div>
      </div>
    );
  }

  // Show controls when clicking on video (but not on controls themselves)
  const handleVideoClick = (e) => {
    // Don't toggle if clicking on controls overlay
    if (e.target.closest('.video-controls-overlay')) {
      return;
    }
    // Show controls on click
    setShowControls(true);
    if (controlsTimeout) {
      clearTimeout(controlsTimeout);
    }
    // Auto-hide after 3 seconds if playing
    if (isPlaying) {
      const timeout = setTimeout(() => {
        setShowControls(false);
      }, 3000);
      setControlsTimeout(timeout);
    }
  };

  return (
    <div 
      ref={containerRef}
      className={`video-player-container ${isFullscreen ? 'video-player-fullscreen' : 'rounded-lg'} ${className}`}
      style={!isFullscreen ? { minHeight: '400px' } : {}}
      tabIndex={0}
      onClick={handleVideoClick}
    >
      {/* Fullscreen Controls */}
      <div className="absolute top-4 right-4 z-20 flex items-center space-x-2">
        {isFullscreen ? (
          <button
            onClick={() => setIsFullscreen(false)}
            className="p-2 bg-black bg-opacity-50 rounded-full hover:bg-opacity-75 transition-all"
            title="Minimize"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        ) : (
          <button
            onClick={() => setIsFullscreen(true)}
            className="p-2 bg-black bg-opacity-50 rounded-full hover:bg-opacity-75 transition-all"
            title="Fullscreen"
          >
            <Maximize className="w-5 h-5 text-white" />
          </button>
        )}
      </div>

      {/* Video Element */}
      {isValidVideoUrl(src) ? (
        <iframe
          src={convertToEmbedUrl(src)}
          title={title}
          className="w-full h-full object-contain"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          allowFullScreen
          onError={(e) => {
            // Don't set error state for iframe issues, just log them
          }}
        />
      ) : (
        <video
          ref={videoRef}
          src={src}
          poster={poster}
          className="w-full h-full object-contain"
          autoPlay={autoPlay}
          preload="metadata"
          onError={(e) => {
            // Only set error for actual video element errors, not network issues
            if (e.target.error && e.target.error.code !== 4) { // MEDIA_ELEMENT_ERROR_NETWORK
              setError(e);
            }
          }}
        />
      )}

      {/* Enhanced Loading Overlay */}
      {isLoading && (
        <div className="video-loading flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm">
          <div className="text-center">
            <div className="relative">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-white border-opacity-20 mx-auto mb-4"></div>
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-transparent border-t-white absolute top-0 left-1/2 transform -translate-x-1/2"></div>
            </div>
            <p className="text-white text-sm font-medium">Loading video...</p>
            <div className="mt-2 w-24 h-1 bg-white bg-opacity-20 rounded-full overflow-hidden">
              <div className="h-full bg-white rounded-full animate-pulse"></div>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Center Play Button */}
      {!isPlaying && !isLoading && !error && (
        <motion.button
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 hover:bg-opacity-50 transition-all duration-300"
          onClick={togglePlay}
        >
          <motion.div 
            className="bg-white bg-opacity-95 rounded-full p-6 shadow-2xl backdrop-blur-sm border border-white border-opacity-20"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <Play className="w-12 h-12 text-black ml-1" />
          </motion.div>
        </motion.button>
      )}

      {/* Enhanced Controls Overlay - Auto-hide when playing */}
      <div 
        className={`video-controls-overlay absolute left-0 right-0 bg-gradient-to-t from-black via-black/90 to-transparent p-6 bottom-0 backdrop-blur-sm transition-opacity duration-300 ${
          showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onMouseMove={(e) => {
          // Prevent event from bubbling to video container
          e.stopPropagation();
          // Show controls on mouse movement
          setShowControls(true);
          if (controlsTimeout) {
            clearTimeout(controlsTimeout);
          }
          // Auto-hide after 3 seconds if playing
          if (isPlaying) {
            const timeout = setTimeout(() => {
              setShowControls(false);
            }, 3000);
            setControlsTimeout(timeout);
          }
        }}
        onMouseLeave={(e) => {
          // Prevent event from bubbling
          e.stopPropagation();
          // Auto-hide when mouse leaves if playing
          if (isPlaying) {
            if (controlsTimeout) {
              clearTimeout(controlsTimeout);
            }
            const timeout = setTimeout(() => {
              setShowControls(false);
            }, 1000);
            setControlsTimeout(timeout);
          }
        }}
        onClick={(e) => {
          // Prevent click from bubbling to video container (which would toggle play)
          e.stopPropagation();
        }}
      >
        {showControls && (
          <>
            {/* Enhanced Progress Bar */}
            <div className="mb-6">
              <div 
                ref={progressRef}
                className="relative h-2 bg-white bg-opacity-20 rounded-full cursor-pointer group"
                onClick={handleProgressClick}
              >
                {/* Buffered Progress */}
                <div 
                  className="absolute h-full bg-white bg-opacity-30 rounded-full transition-all duration-300"
                  style={{ width: `${buffered}%` }}
                />
                {/* Played Progress */}
                <div 
                  className="absolute h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full transition-all duration-300"
                  style={{ width: `${(currentTime / duration) * 100}%` }}
                />
                {/* Progress Handle */}
                <motion.div 
                  className="absolute top-1/2 transform -translate-y-1/2 w-4 h-4 bg-white rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                  style={{ left: `${(currentTime / duration) * 100}%`, marginLeft: '-8px' }}
                  whileHover={{ scale: 1.2 }}
                />
              </div>
            </div>

            {/* Main Controls */}
            <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {/* Enhanced Skip Backward */}
            <motion.button
              onClick={() => skipTime(-10)}
              className="text-white hover:text-blue-400 transition-colors p-2 rounded-full hover:bg-white hover:bg-opacity-10"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <SkipBack className="w-5 h-5" />
            </motion.button>

            {/* Enhanced Play/Pause - In the middle */}
            <motion.button
              onClick={togglePlay}
              className="text-white hover:text-blue-400 transition-colors p-2 rounded-full hover:bg-white hover:bg-opacity-10"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              {isPlaying ? <Pause className="w-7 h-7" /> : <Play className="w-7 h-7" />}
            </motion.button>

            {/* Enhanced Skip Forward */}
            <motion.button
              onClick={() => skipTime(10)}
              className="text-white hover:text-blue-400 transition-colors p-2 rounded-full hover:bg-white hover:bg-opacity-10"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <SkipForward className="w-5 h-5" />
            </motion.button>

            {/* Enhanced Volume Control */}
            <div className="flex items-center space-x-3 group">
              <motion.button
                onClick={toggleMute}
                className="text-white hover:text-blue-400 transition-colors p-2 rounded-full hover:bg-white hover:bg-opacity-10"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                {isMuted || volume === 0 ? (
                  <VolumeX className="w-5 h-5" />
                ) : volume < 0.33 ? (
                  <Volume className="w-5 h-5" />
                ) : volume < 0.66 ? (
                  <Volume1 className="w-5 h-5" />
                ) : (
                  <Volume2 className="w-5 h-5" />
                )}
              </motion.button>
              <div className="relative">
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={isMuted ? 0 : volume}
                  onChange={handleVolumeChange}
                  className="w-20 h-1.5 bg-white bg-opacity-20 rounded-full appearance-none cursor-pointer slider group-hover:w-24 transition-all duration-300"
                  style={{
                    background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(isMuted ? 0 : volume) * 100}%, rgba(255,255,255,0.2) ${(isMuted ? 0 : volume) * 100}%, rgba(255,255,255,0.2) 100%)`
                  }}
                />
              </div>
            </div>

            {/* Enhanced Time Display */}
            <div className="text-white text-sm font-medium bg-black bg-opacity-30 px-3 py-1 rounded-full">
              {formatTime(currentTime)} / {formatTime(duration)}
            </div>
          </div>

          <div className="flex items-center space-x-3">
            {/* Enhanced Notes Toggle */}
            <motion.button
              onClick={() => setShowNotes(!showNotes)}
              className={`text-white transition-colors p-2 rounded-full ${
                showNotes 
                  ? 'bg-blue-600 text-white' 
                  : 'hover:text-blue-400 hover:bg-white hover:bg-opacity-10'
              }`}
              title="Show Notes"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              {showNotes ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </motion.button>

            {/* Enhanced Discussion Toggle */}
            {lectureId && (
              <motion.button
                onClick={() => onDiscussionToggle && onDiscussionToggle(!showDiscussion)}
                className={`text-white transition-colors p-2 rounded-full ${
                  showDiscussion 
                    ? 'bg-purple-600 text-white' 
                    : 'hover:text-purple-400 hover:bg-white hover:bg-opacity-10'
                }`}
                title="Show Discussion"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <MessageSquare className="w-5 h-5" />
              </motion.button>
            )}

            {/* Enhanced Settings */}
            <div className="relative">
              <motion.button
                onClick={() => setShowSettings(!showSettings)}
                className={`text-white transition-colors p-2 rounded-full ${
                  showSettings 
                    ? 'bg-blue-600 text-white' 
                    : 'hover:text-blue-400 hover:bg-white hover:bg-opacity-10'
                }`}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <Settings className="w-5 h-5" />
              </motion.button>

              {/* Enhanced Settings Dropdown */}
              <AnimatePresence>
                {showSettings && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 10 }}
                    className="absolute bottom-full right-0 mb-3 bg-gray-900 bg-opacity-95 backdrop-blur-sm rounded-xl p-4 min-w-56 border border-gray-700 shadow-2xl"
                  >
                    {/* Enhanced Playback Speed */}
                    <div className="mb-4">
                      <div className="text-white text-sm font-medium mb-3 flex items-center">
                        <Settings className="w-4 h-4 mr-2" />
                        Playback Speed
                      </div>
                      <div className="grid grid-cols-4 gap-2">
                        {playbackSpeeds.map((speed) => (
                          <motion.button
                            key={speed}
                            onClick={() => handlePlaybackRateChange(speed)}
                            className={`px-3 py-2 text-xs rounded-lg font-medium transition-all duration-200 ${
                              playbackRate === speed
                                ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg'
                                : 'bg-gray-700 text-white hover:bg-gray-600 hover:scale-105'
                            }`}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            {speed}x
                          </motion.button>
                        ))}
                      </div>
                    </div>

                    {/* Enhanced Quality */}
                    <div>
                      <div className="text-white text-sm font-medium mb-3 flex items-center">
                        <Video className="w-4 h-4 mr-2" />
                        Quality
                      </div>
                      <div className="space-y-2">
                        {qualityOptions.map((option) => (
                          <motion.button
                            key={option.value}
                            onClick={() => handleQualityChange(option.value)}
                            className={`w-full text-left px-3 py-2 text-xs rounded-lg font-medium transition-all duration-200 ${
                              currentQuality === option.value
                                ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg'
                                : 'bg-gray-700 text-white hover:bg-gray-600 hover:scale-105'
                            }`}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            {option.label}
                          </motion.button>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Enhanced Fullscreen */}
            <motion.button
              onClick={toggleFullscreen}
              className="text-white hover:text-blue-400 transition-colors p-2 rounded-full hover:bg-white hover:bg-opacity-10"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
            </motion.button>
          </div>
        </div>
          </>
        )}
      </div>

             {/* Notes Panel - Now shows PDF attachments */}
       <AnimatePresence>
         {showNotes && (
           <>
             {/* Backdrop */}
             <motion.div
               initial={{ opacity: 0 }}
               animate={{ opacity: 1 }}
               exit={{ opacity: 0 }}
               className="absolute inset-0 bg-black bg-opacity-25 z-20"
               onClick={() => setShowNotes(false)}
             />
             {/* Panel */}
             <motion.div
               initial={{ x: '100%' }}
               animate={{ x: 0 }}
               exit={{ x: '100%' }}
               className="absolute top-0 right-0 w-80 h-full bg-white shadow-lg z-30"
             >
            <div className="p-4 h-full flex flex-col">
                             <div className="flex items-center justify-between mb-4">
                 <h3 className="text-lg font-semibold text-gray-900">Lecture Notes</h3>
                 <button
                   onClick={() => setShowNotes(false)}
                   className="p-1 rounded-full hover:bg-gray-100 transition-colors"
                   title="Close"
                 >
                   <X className="w-5 h-5 text-gray-500 hover:text-gray-700" />
                 </button>
               </div>

              {/* PDF Attachments */}
              <div className="flex-1 overflow-y-auto">
                {attachments && attachments.length > 0 ? (
                  <div className="space-y-3">
                    {attachments.map((attachment, index) => (
                      <div key={index} className="p-3 bg-gray-50 rounded-lg border">
                        <div className="flex items-center space-x-3">
                          <FileText className="w-8 h-8 text-red-500" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {attachment.name || `PDF ${index + 1}`}
                            </p>
                            {attachment.size && (
                              <p className="text-xs text-gray-500">
                                {attachment.size}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 mt-3">
                          <button
                            onClick={() => window.open(attachment.url, '_blank')}
                            className="flex-1 px-3 py-2 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition-colors flex items-center justify-center space-x-1"
                          >
                            <Eye className="w-4 h-4" />
                            <span>View</span>
                          </button>

                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-center">
                    <FileText className="w-16 h-16 text-gray-300 mb-4" />
                    <p className="text-gray-500 text-lg font-medium">No notes are here</p>
                    <p className="text-gray-400 text-sm mt-2">
                      No PDF attachments available for this lecture
                    </p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Discussion Panel */}
      <AnimatePresence>
        {showDiscussion && lectureId && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black bg-opacity-25 z-20"
              onClick={() => onDiscussionToggle && onDiscussionToggle(false)}
            />
            {/* Panel */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              className="absolute top-0 right-0 w-96 h-full bg-white shadow-lg z-30"
            >
              <DiscussionPanel
                lectureId={lectureId}
                currentTime={currentTime}
                currentUser={currentUser}
                onClose={() => onDiscussionToggle && onDiscussionToggle(false)}
                formatTime={formatTime}
              />
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Enhanced Video Title */}
      <div className="absolute top-4 left-4 z-10">
        <div className="bg-black bg-opacity-50 backdrop-blur-sm rounded-lg px-4 py-2 border border-white border-opacity-20">
          <h2 className="text-white text-lg font-bold drop-shadow-lg">
            {title}
          </h2>
          {isEmbeddedVideo && (
            <div className="text-white text-sm opacity-90 mt-1 drop-shadow-lg flex items-center">
              <Video className="w-3 h-3 mr-1" />
              {getVideoPlatform(src)} Video
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoPlayer;
